import Vue from 'vue';
import { API_ENDPOINTS, EMAIL_NOTIFICATION, CRON , MESSAGES} from '../utils/constants';
import constantsModule from '@/plugins/constants';

// Define baseurl for use in actions, fallback to window.location.origin if not set
const baseurl = process.env.VUE_APP_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

// Use Vue.axios which is set up by VueAxios plugin in main.js
const $axios = Vue.axios;
// Lazy accessor for $nova to avoid race condition with plugin initialization
function getNova() {
  return Vue.prototype.$nova;
}
const $constants = Vue.prototype.$constants || constantsModule.default || constantsModule;

// Internal helper to apply reconciliation flag (isReconFound) returned from
// backend responses after file uploads (single or bulk). Accepts one task
// object or an array of task objects. Silently no-ops if flag missing.
function applyReconFlag({ commit, state }, { responseData, tasks, checklistId }) {
  try {
    const flagObj = Array.isArray(responseData?.data) ? responseData.data[0] : null;
    if (!flagObj || !Object.prototype.hasOwnProperty.call(flagObj, 'isReconFound')) return;
    const reconFlag = !!flagObj.isReconFound;
    const taskArray = Array.isArray(tasks) ? tasks : (tasks ? [tasks] : []);
    taskArray.forEach(t => {
      if (!t || !t._id) return;
      if (checklistId) {
        commit('UPDATE_TASK', { checklistId, taskId: t._id, updates: { isReconFound: reconFlag } });
      }
      if (state.currentTask && state.currentTask._id === t._id) {
        commit('SET_CURRENT_TASK', { ...state.currentTask, isReconFound: reconFlag });
      }
    });
  } catch (e) {
    // Swallow intentionally – flag application is best-effort and must not block main flow
  }
}

export default {
  namespaced: true,
  
  state: {
    tasks: {},  // Keyed by taskId for quick access
    currentTask: null,
    loading: false,
    error: null,
    taskLists: {}, // Keyed by projectId/checklistId
    // Initialize empty task list data with all expected properties
    taskListData: {
      metadata: null,
      checklist: [],
      pbcitems: [],
      summary: {
        manager: {},
        preparer: {},
        approver: {}
      },
      locationList: [],
      usersList: [],
      locationThresholds: [],
      infomangerrequests: [],
      chartAcc: [],
      coaCategoryAcc: [],
      coaSubcategoryAcc: [],
      categoryAcc: [],
      subcategoryAcc: [],
      customColumns: {}
    },
    managersummary: {},
    preparersummary: {},
    approversummary: {},
    locationList: [],
    enabledLocations: [],
    usersList: [],
    locationThresholds: [],
    infomangerrequests: [],
    chartAcc: [],
    tasklistLoaded: false,
    notificationEnabled: false,
    // Custom layouts keyed by page name (e.g. financialclose_master_checklist)
    customLayouts: {},
    statusDetails: null,
    transactionMatchingList: null,
    // For email notifications
    emailNotifications: [],
    isCycleInActive: false,
    // UI flags for bulk upload
    drawerVisible: false,
    bulkworkpaperupload: false,
    bulkfilesloading: 0,
    sendForReviewDialogbulk: false,
    sendforreviewdrawer: false,
    userDetails:{},
    activeUsers:[],
    inactiveUsers:[],
    masterCategories: {
      categoryAcc: [],
      subcategoryAcc: [],
    },
    filteredLocations: [],
    filteredLocationsCycleId: null,
    // Horizon UI state keyed by checklistId (survives back navigation)
    horizonViewState: {},
    // Last List/Horizon view per checklist (restored on task-detail back navigation)
    tasklistViewByChecklist: {},
    // v3 formdata cache (client-level reference data)
    formDataV3: null,
    // TB pagination state (for GL picker infinite scroll - Phase 2)
    tbPagination: { page: 1, limit: 50, totalCount: 0, totalPages: 0 },
    tbRecords: [],
    tbLoading: false,
    tbFullyLoaded: false,
  },
  
  mutations: {
    RESET_BULK_UPLOAD_UI(state) {
      state.drawerVisible = false;
      state.bulkworkpaperupload = false;
      state.bulkfilesloading = 1;
      state.sendForReviewDialogbulk = false;
      state.sendforreviewdrawer = false;
    },
    SET_DRAWER_VISIBLE(state, value) {
      state.drawerVisible = value;
    },
    SET_SEND_FOR_REVIEW_DRAWER(state, value) {
      state.sendforreviewdrawer = value;
    },
    CLEAR_TASKLIST(state) {
      // Reset to initial state with empty arrays instead of undefined values
      state.taskListData = {
        metadata: null,
        checklist: [],
        tb: [],
        pbcitems: [],
        summary: {
          manager: {},
          preparer: {},
          approver: {}
        },
        locationList: [],
        usersList: [],
        locationThresholds: [],
        infomangerrequests: [],
        chartAcc: [],
        coaCategoryAcc: [],
        coaSubcategoryAcc: [],
        categoryAcc: [],
        subcategoryAcc: [],
        customColumns: {}
      };
      // Reset filtered locations so they are recomputed for the next cycle
      state.filteredLocations = [];
      state.filteredLocationsCycleId = null;
      // Phase 2: Also reset TB pagination state
      state.tbRecords = [];
      state.tbPagination = { page: 1, limit: 50, totalCount: 0, totalPages: 0 };
      state.tbLoading = false;
      state.tbFullyLoaded = false;
    },
    SET_TASKLIST_LOADED(state, value) {
      state.tasklistLoaded = value;
    },
    SET_EMAIL_NOTIFICATIONS(state, notifications) {
      state.emailNotifications = notifications || [];
    },
    SET_CYCLE_STATUS(state, isInactive) {
      state.isCycleInActive = isInactive;
    },
    SET_MANAGER_SUMMARY(state, payload) {
      state.managersummary = payload;
    },
    SET_PREPARER_SUMMARY(state, payload) {
      state.preparersummary = payload;
    },
    SET_APPROVER_SUMMARY(state, payload) {
      state.approversummary = payload;
    },
    SET_LOCATION_LIST(state, payload) {
      state.locationList = payload;
    },
    SET_ENABLED_LOCATIONS(state, payload) {
      state.enabledLocations = payload;
    },
    SET_USERS_LIST(state, payload) {
      state.usersList = payload;
    },
    SET_LOCATION_THRESHOLDS(state, payload) {
      state.locationThresholds = payload;
    },
    SET_INFOMANAGER_REQUESTS(state, payload) {
      state.infomangerrequests = payload;
    },
    SET_CHART_ACC(state, payload) {
      state.chartAcc = payload;
    },
    SET_CUSTOM_LAYOUT(state, payload) {
      if (payload === null) {
        state.customLayouts = {};
        return;
      }
      const page = payload?.page;
      if (!page) return;
      Vue.set(state.customLayouts, page, payload);
    },
    SET_CUSTOM_LAYOUT_PAGE(state, { page, data }) {
      if (!page) return;
      Vue.set(state.customLayouts, page, data);
    },
    MERGE_CUSTOM_LAYOUT_PAGE(state, { page, custom_layout, scopedKey, filter }) {
      if (!page || !scopedKey) return;
      const existing = state.customLayouts[page] || {};
      const existingFilters =
        existing.filter && typeof existing.filter === 'object' ? existing.filter : {};
      Vue.set(state.customLayouts, page, {
        ...existing,
        custom_layout,
        filter: { ...existingFilters, [scopedKey]: filter || {} },
        page,
      });
    },
    CLEAR_CUSTOM_LAYOUT_PAGE(state, page) {
      if (!page) return;
      Vue.delete(state.customLayouts, page);
    },
    SET_STATUS_DETAILS(state, payload) {
      state.statusDetails = payload;
    },
    SET_TRANSACTION_MATCHING_LIST(state, payload) {
      state.transactionMatchingList = payload;
    },
    // Set the current task
    SET_CURRENT_TASK(state, task) {
      if (!task) {
        state.currentTask = task;
        return;
      }

      // Keep a single source of truth for the task object across:
      // - state.currentTask (task details page)
      // - state.tasks[taskId] (task cache used by getTaskById)
      //
      // This prevents stale cached tasks from overwriting the latest currentTask.
      if (task && task._id) {
        const previousCached = state.tasks[task._id] || {};
        const merged = { ...previousCached, ...task };
        Vue.set(state.tasks, task._id, merged);
        state.currentTask = merged;
      } else {
        state.currentTask = task;
      }
    },

    SET_ALL_USERS_LIST(state, users) {
      state.userDetails = users || [];
    },
    SET_ACTIVE_USERS(state, users) {
      state.activeUsers = users || [];
    },
    SET_INACTIVE_USERS(state, users) {
      state.inactiveUsers = users || [];
    },
    SET_MASTER_CATEGORIES(state, { categoryAcc, subcategoryAcc }) {
      state.masterCategories = {
        categoryAcc: categoryAcc || [],
        subcategoryAcc: subcategoryAcc || [],
      };
    },
    SET_FILTERED_LOCATIONS(state, { locations, cycleId } = {}) {
      state.filteredLocations = locations || [];
      if (cycleId !== undefined) state.filteredLocationsCycleId = cycleId;
    },
    SET_HORIZON_VIEW_STATE(state, { checklistId, partial }) {
      if (!checklistId) return;
      state.horizonViewState = {
        ...state.horizonViewState,
        [checklistId]: {
          ...(state.horizonViewState[checklistId] || {}),
          ...partial,
        },
      };
    },
    CLEAR_HORIZON_VIEW_STATE(state, checklistId) {
      if (!checklistId) return;
      const next = { ...state.horizonViewState };
      delete next[checklistId];
      state.horizonViewState = next;
    },
    SET_TASKLIST_VIEW(state, { checklistId, view }) {
      if (!checklistId || !view) return;
      state.tasklistViewByChecklist = {
        ...state.tasklistViewByChecklist,
        [checklistId]: view,
      };
    },
    SET_FORM_DATA_V3(state, data) {
      state.formDataV3 = data;
    },
    // TB pagination mutations (Phase 2)
    SET_TB_RECORDS(state, { records, append }) {
      if (append) {
        state.tbRecords = [...state.tbRecords, ...records];
      } else {
        state.tbRecords = records;
      }
    },
    SET_TB_PAGINATION(state, pagination) {
      state.tbPagination = { ...state.tbPagination, ...pagination };
    },
    SET_TB_LOADING(state, loading) {
      state.tbLoading = loading;
    },
    SET_TB_FULLY_LOADED(state, fullyLoaded) {
      state.tbFullyLoaded = fullyLoaded;
    },
    RESET_TB_STATE(state) {
      state.tbRecords = [];
      state.tbPagination = { page: 1, limit: 50, totalCount: 0, totalPages: 0 };
      state.tbLoading = false;
      state.tbFullyLoaded = false;
    },
    
    // Store task in cache
    CACHE_TASK(state, task) {
      if (task && task._id) {
        Vue.set(state.tasks, task._id, task);
      }
    },
    
    // Set the loading state
    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    
    // Set error state
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    // Update or store a task list
    SET_TASK_LIST(state, { checklistId, tasks }) {
      Vue.set(state.taskLists, checklistId, tasks);
      
      // Also cache individual tasks for quick access
      if (Array.isArray(tasks)) {
        tasks.forEach(task => {
          if (task && task._id) {
            Vue.set(state.tasks, task._id, task);
          }
        });
      }
    },
    
    // Update a single task in both tasks and taskLists
    UPDATE_TASK(state, { checklistId, taskId, updates }) {
      // Update in tasks cache
      if (state.tasks[taskId]) {
        Vue.set(state.tasks, taskId, { ...state.tasks[taskId], ...updates });
      } else if (updates && (updates._id || taskId)) {
        // Best-effort populate cache if it didn't exist yet
        Vue.set(state.tasks, taskId, { ...updates, _id: updates._id || taskId });
      }
      
      // Update in taskLists if it exists
      if (state.taskLists[checklistId]) {
        const index = state.taskLists[checklistId].findIndex(task => task._id === taskId);
        if (index !== -1) {
          Vue.set(state.taskLists[checklistId], index, {
            ...state.taskLists[checklistId][index],
            ...updates
          });
        }
      }
      
      // Update currentTask if it's the same task
      if (state.currentTask && state.currentTask._id === taskId) {
        state.currentTask = { ...state.currentTask, ...updates };
      }
    },
    
    /**
     * Set the entire tasklist data
     * This is used for maintaining tasklist data across views
     */
    SET_TASKLIST_DATA(state, data) {
      // Store the entire tasklist data
      state.taskListData = data;

      if (data?.summary) {
        state.managersummary = data.summary.manager || {};
        state.preparersummary = data.summary.preparer || {};
        state.approversummary = data.summary.approver || {};
      }

      // Keep the per-task cache aligned with the latest checklist payload.
      // This avoids stale state.tasks[taskId] overwriting state.currentTask later.
      const checklist = data && Array.isArray(data.checklist) ? data.checklist : [];
      checklist.forEach(task => {
        if (task && task._id) {
          const previousCached = state.tasks[task._id] || {};
          Vue.set(state.tasks, task._id, { ...previousCached, ...task });
        }
      });
    },
    SET_NOTIFICATION_ENABLED(state, value) {
      state.notificationEnabled = value;
    },
  },
  
  actions: {
    /**
     * Add or edit a checklist item (used by Tasklist.vue, TaskEditDetails.vue, and TaskGLSelection.vue)
     * @param {Object} payload { addeditdata, customColumns, selectglsLocal, projectId }
     * projectId is required for "add" mode (when addeditdata._id is absent)
     * @returns {Object} result { success, data, errorMessage }
     */
    async addEditChecklistItem(_, { addeditdata, customColumns, selectglsLocal, projectId }) {
      try {
        // Fallback if $constants not injected in this scope
        const localConstants = $constants;
        // Clean undefined fields
        Object.keys(addeditdata).forEach(key => {
          if (addeditdata[key] === undefined) delete addeditdata[key];
        });
        // Prepare customfields
        let customfields = [];
        if (Array.isArray(customColumns) && customColumns.length > 0) {
          customfields = customColumns.map(column => ({
            [column.columnName]:
              addeditdata[column.columnName] !== undefined
                ? addeditdata[column.columnName]
                : column.dataType === 'number' ? 0 : ''
          }));
        }
        addeditdata.customfields = customfields;
        // Remove individual custom column keys from top-level (they are now in customfields)
        if (Array.isArray(customColumns) && customColumns.length > 0) {
          customColumns.forEach(column => {
            delete addeditdata[column.columnName];
          });
        }
        // Validate no_of_evidence
        if (typeof addeditdata.no_of_evidence !== 'number' || addeditdata.no_of_evidence < 0 || addeditdata.no_of_evidence > 10) {
          return { success: false, errorMessage: 'Please enter a value between 0 and 10.', data: { index0: { no_of_evidence: ['Please enter a value between 0 and 10.'] } } };
        }
        // Validate task_type
        const TASK_TYPES = (localConstants || {}).TASK_LIST_CONSTANTS?.TASK_TYPES || {};
        if (!Object.values(TASK_TYPES).includes(addeditdata.task_type)) {
          return { success: false, errorMessage: 'Task Type is required. Please select a value.', data: { index0: { task_type: ['Task Type is required. Please select a value.'] } } };
        }
        // Validate priority
        if (!addeditdata.priority) {
          return { success: false, errorMessage: 'Priority is required. Please select a value.', data: { index0: { priority: ['Priority is required. Please select a value.'] } } };
        }
        // Validate GL selection
        if (addeditdata.task_type === TASK_TYPES.NORMAL) {
          if (selectglsLocal && selectglsLocal.length) {
            return { success: false, errorMessage: 'GL selection is not allowed for Normal Task Type.', data: { index0: { glcode: ['Not Allowed'] } } };
          }
        } else if (addeditdata.task_type === TASK_TYPES.ACCOUNT_RECONCILIATION) {
          if (!selectglsLocal || !selectglsLocal.length) {
            const ERR_MSG = (localConstants || {}).TASK_LIST_CONSTANTS?.ERROR_MESSAGES?.GL_CODE_REQUIRED || 'At least one GL required.';
            return { success: false, errorMessage: 'Account selection is required for Account Reconciliation.', data: { index0: { glcode: ERR_MSG } } };
          }
        }
        // Prepare payload for backend
        let ndt = { ...addeditdata };
        ndt.glcode = selectglsLocal;
        ndt.__tblinks = Array.isArray(selectglsLocal) ? [...selectglsLocal] : [];
        const isEdit = !!addeditdata._id;
        delete ndt._id;
        delete ndt.customColumns;
        const url = isEdit
          ? `/v2/financialclose/analysis/editchecklist/${addeditdata._id}`
          : `/v2/financialclose/analysis/addchecklist/${projectId}`;
        const response = await $axios.post(url, { data: ndt });
        if (response.data.status === 'success') {
          return { success: true, data: ndt, isEdit };
        } else {
          if (response.data.message) {
            return { success: false, errorMessage: response.data.message };
          } else if (response.data.data && response.data.data.length > 0 && response.data.data[0].index0) {
            return { success: false, data: response.data.data[0] };
          } else {
            throw new Error(response.data.message || 'Unknown error!');
          }
        }
      } catch (err) {
        return { success: false, errorMessage: err.message || 'Unknown error!' };
      }
    },
    async submitTask({ commit }, { taskId, cycleId, suppressSnackbar = false }) {
      try {
        if (!cycleId || !taskId) throw new Error('Missing cycle or task ID');
        const url = `/v2/financialclose/analysis/submitTask/${taskId}/c/${cycleId}`;
        const payload = {};
        const response = await $axios.post(url, payload);
        if (response?.data?.status === 'success') {
          if (!suppressSnackbar) {
            commit('sbSuccess', 'Task submitted successfully', { root: true });
          }
          return { status: 'success', data: response.data };
        } else {
          if (!suppressSnackbar) {
            commit('sbError', response?.data?.message || 'Failed to submit task', { root: true });
          }
          return { status: 'error', message: response?.data?.message };
        }
      } catch (err) {
        if (!suppressSnackbar) {
          commit('sbError', err?.message || 'Error submitting task', { root: true });
        }
        return { status: 'error', message: err?.message };
      }
    },
    async deleteFileFromTask({ commit, rootState }, { file, task }) {
      try {
        const projectId = task.project;
        const clientId = rootState.client;
        const taskId = task._id;
        // Support both _id and file_id from file object
        const fileId = file?._id || file?.file_id;
        if (!projectId || !clientId || !taskId || !fileId) throw new Error('Missing required IDs');
        const url = `/v2/financialclose/analysis/deleteFileFromTask/${projectId}`;
        const payload = {
          client: clientId,
          data: {
            fileid: fileId,
            taskid: taskId
          }
        };
        const response = await $axios.post(url, payload);
        if (response?.data?.status === 'success') {
          commit('sbSuccess', 'File deleted successfully', { root: true });
          return { success: true };
        } else {
          commit('sbError', response?.data?.message || 'Failed to delete file', { root: true });
          return { success: false, message: response?.data?.message };
        }
      } catch (err) {
        commit('sbError', err?.message || 'Error deleting file', { root: true });
        return { success: false, message: err?.message };
      }
    },
    async getUsersFormData({ commit }) {
      try {
        const response = await $axios.post("/v2/financialclose/analysis/getusersformdata");
        if (response.data.status === "success") {
          // Extract users array from response
          const users = (response.data.data && response.data.data[0] && response.data.data[0].users) || [];
          commit('SET_USERS_LIST', users);
        } else {
          commit('SET_USERS_LIST', []);
          commit('sbError', response.data.message || "Failed to fetch users", { root: true });
        }
      } catch (err) {
        commit('SET_USERS_LIST', []);
        commit('sbError', err.message || "Failed to fetch users", { root: true });
      }
    },
    /**
     * Fetch enabled locations for dropdown
     * @returns {Object} { success, data }
     */
    async fetchEnabledLocations({ commit }) {
      try {
        const response = await $axios.post("/v2/financialclose/location/getenabledlocation");
        if (response.data.status === "success") {
          const locations = response.data.data || [];
          commit('SET_ENABLED_LOCATIONS', locations);
          return { success: true, data: locations };
        } else {
          commit('SET_ENABLED_LOCATIONS', []);
          commit('sbError', response.data.message || "Failed to fetch enabled locations", { root: true });
          return { success: false };
        }
      } catch (err) {
        console.error('[StoreAction]: fetchEnabledLocations error', err);
        commit('SET_ENABLED_LOCATIONS', []);
        commit('sbError', err?.response?.data?.message || err?.message || "Failed to fetch enabled locations", { root: true });
        return { success: false };
      }
    },
    /**
     * Fetch client-level reference data via /v3/ consolidated endpoint.
     * Caches result in store — returns cached data unless forceRefresh is true.
     * Replaces: T1 (getAllUsersForEntity), T2 (getenabledlocation), T4 (getformdata),
     *           T5 (location/get), T8 (chartofaccount/get), T9 (uniquecategories), T10 (client/get)
     * @param {Object} payload { forceRefresh }
     * @returns {Object} { users, locations, coaCategoryAcc, coaSubcategoryAcc, categoryAcc, subcategoryAcc, schedulerEnabled }
     */
    async fetchFormDataV3({ commit, state, rootState }, { forceRefresh = false } = {}) {
      if (!forceRefresh && state.formDataV3) {
        commit(
          'SET_ENABLED_LOCATIONS',
          (state.formDataV3.locations || []).filter(location => location && location.enabled !== false)
        );
        return state.formDataV3;
      }
      try {
        const clientId = rootState.client;
        const response = await $axios.post('/v3/financialclose/analysis/getformdata', { client: clientId });
        if (response?.data?.status === 'success') {
          const formData = response.data.data;
          commit('SET_FORM_DATA_V3', formData);
          // Also populate legacy state slices for backward compatibility
          const allUsers = formData.users || [];
          const activeUsers = allUsers.filter(u => u.isActive);
          const inactiveUsers = allUsers.filter(u => !u.isActive);
          const availableLocations = (formData.locations || []).filter(location => location && location.enabled !== false);
          commit('SET_ACTIVE_USERS', activeUsers);
          commit('SET_INACTIVE_USERS', inactiveUsers);
          commit('SET_ENABLED_LOCATIONS', availableLocations);
          commit('SET_NOTIFICATION_ENABLED', formData.schedulerEnabled || false);
          return formData;
        } else {
          throw new Error(response?.data?.message || 'Failed to fetch form data');
        }
      } catch (err) {
        console.error('[StoreAction]: fetchFormDataV3 error', err);
        commit('sbError', err?.response?.data?.message || err?.message || 'Failed to fetch reference data', { root: true });
        return null;
      }
    },
    /**
     * Ensure filteredLocations are populated in the store.
     * If already available and matching the cycleId, no-op.
     * Otherwise fetches locations via v3 formdata + cycle metadata to compute the filtered set.
     * Used by Threshold and Dashboard pages that may load independently of Tasklist.
     * @param {Object} payload { cycleId }
     */
    async ensureFilteredLocations({ commit, state, dispatch }, { cycleId }) {
      // If already populated for this cycle, no-op
      if (state.filteredLocations && state.filteredLocations.length > 0 && state.filteredLocationsCycleId === cycleId) return;

      try {
        // Get locations from v3 formdata (cached)
        const formData = state.formDataV3 || await dispatch('fetchFormDataV3');
        const allLocations = formData?.locations || [];

        // Get cycle locations from cached metadata or fetch minimal cycle info
        let cycleLocations = [];
        const metadata = state.taskListData?.metadata;
        if (metadata && metadata._id === cycleId) {
          cycleLocations = metadata.location || [];
        } else {
          const res = await $axios.post(`/v3/financialclose/analysis/getdetails/${cycleId}`, { page: 'tasklist' });
          if (res.data?.status === "success") {
            cycleLocations = res.data.data?.metadata?.location || [];
          }
        }

        const filtered = cycleLocations.length === 0
          ? allLocations.map(loc => ({ _id: loc._id, name: loc.name }))
          : allLocations.filter(loc => cycleLocations.includes(loc._id))
                         .map(loc => ({ _id: loc._id, name: loc.name }));
        commit('SET_FILTERED_LOCATIONS', { locations: filtered, cycleId });
      } catch (err) {
        console.error('[StoreAction]: ensureFilteredLocations error', err);
      }
    },
  /**
   * Bulk upload files for tasks (from TaskUploadModal.vue)
   * @param {Object} payload { files, id, selectItem, viewtaskdata, remark, suppressSnackbar }
   */
    async uploadFilesBulk({ commit, state }, { files, id, selectItem, viewtaskdata, remark, suppressSnackbar = false }) {
      let totalSize = 0;
      let dataArrayList = [];
      try {
        if (Array.isArray(files) && files.length > 0) {
          files.forEach((file) => {
            if (file.name) {
              totalSize += file.size;
            }
          });
          const MAX_TOTAL_SIZE = 25 * 1024 * 1024;
          if (totalSize > MAX_TOTAL_SIZE) {
            commit('sbError', `Total file size exceeds the limit (${(MAX_TOTAL_SIZE / (1024 * 1024)).toFixed(2)} MB)`, { root: true });
            return;
          }
          const $novaLocal = getNova();
          if (!$novaLocal || typeof $novaLocal.uploadFile !== 'function') {
            commit('sbError', 'File upload service not initialized', { root: true });
            return;
          }
          const uploadPromises = files.map(async (file) => {
            if (file.name) {
              try {
                let response = await $novaLocal.uploadFile($axios, file, {});
                dataArrayList.push(response);
              } catch (uploadError) {
                commit('sbError', uploadError.message || 'Error uploading file', { root: true });
                console.error(uploadError);
              }
            }
          });
          await Promise.all(uploadPromises);
          if (dataArrayList.some((response) => response.data.status === 'success')) {
            const dataArray = dataArrayList.flatMap((item) =>
              item.data.data.flatMap((item) => {
                return {
                  filename: item.uploadname,
                  file_id: item._id,
                  file_path: item.path,
                };
              })
            );
            // Build checklist list (full objects as in legacy Tasklist.vue implementation)
            const selectedTasksRaw = (Array.isArray(selectItem) && selectItem.length)
              ? selectItem
              : (viewtaskdata ? [viewtaskdata] : []);

            // If only IDs are desired (lean payload), map to IDs. For now keep full objects to match old working flow.
            // To switch to IDs later, replace checklist: selectedTasksRaw with checklist: selectedTasksRaw.map(t=>t && t._id).filter(Boolean)
            const data = {
              bulkupload: true,
              upload: dataArray,
              project: id,
              checklist: selectedTasksRaw,
              remark: remark,
              removelocalfile: false,
            };
            try {
              let response = await $axios.post(
                '/v2/financialclose/analysis/bulkStoreWorkPaper',
                { data: [data] }
              );
              if (response.data.status === 'success') {
                if (!suppressSnackbar) {
                  commit('sbSuccess', 'Files uploaded', { root: true });
                }
                commit('RESET_BULK_UPLOAD_UI');
                // Apply reconciliation flag (deduplicated logic)
                const reconTargets = (selectItem && selectItem.length ? selectItem : [viewtaskdata]);
                applyReconFlag({ commit, state }, { responseData: response.data, tasks: reconTargets, checklistId: id });
                return { success: true, data: response.data };
              } else {
                throw new Error(response?.data?.message || 'Error Uploading File');
              }
            } catch (axiosError) {
              commit('sbError', axiosError.message || 'Unknown error!', { root: true });
              return { success: false, error: axiosError.message };
            }
          } else {
            commit('sbError', 'Unable to upload file', { root: true });
            return { success: false, error: 'upload_failed' };
          }
        } else {
          commit('sbError', 'No files to upload', { root: true });
          return { success: false, error: 'no_files' };
        }
      } catch (error) {
        console.error(error);
        return { success: false, error: error.message };
      } finally {
        commit('RESET_BULK_UPLOAD_UI');
      }
    },
  /**
   * Get conversation/comments for a task (from TaskDetailsTabbed.vue)
   * @param {Object} payload { metadataId, taskId }
   */
    async getConversation({ commit }, { metadataId, taskId }) {
      if (!metadataId || !taskId) {
        commit('sbError', 'Missing metadataId or taskId for getConversation', { root: true });
        return { success: false, error: 'Missing metadataId or taskId' };
      }
      let url = `/v2/financialclose/analysis/getconversation/${metadataId}/c/${taskId}`;
      try {
        const dt = await $axios.post(url);
        if (dt.data.status === 'success') {
          // Store comments in the specific task object in state
          const comments = dt.data.data;
          // Update in tasks cache
          if (comments && Array.isArray(comments)) {
            if (this.tasks && this.tasks[taskId]) {
              this.tasks[taskId].comments = comments;
            }
            // Also update currentTask if it's the same
            if (this.currentTask && this.currentTask._id === taskId) {
              this.currentTask.comments = comments;
            }
          }
          return { success: true, data: comments };
        } else {
          throw new Error(dt.data.message || 'Unknown error');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error(err);
        return { success: false, error: err.message || err || 'Unknown error!' };
      }
    },
  /**
   * Add a conversation/comment to a task (from Comments.vue)
   * @param {Object} payload { metadataId, taskId, dataObject }
   */
    async addConversation({ commit }, { metadataId, taskId, dataObject }) {
      if (!metadataId || !taskId) {
        commit('sbError', 'Missing metadataId or taskId for conversation', { root: true });
        return { success: false };
      }
      let url = `/v2/financialclose/analysis/addconversation/${metadataId}/c/${taskId}`;
      try {
        const dt = await $axios.post(url, { data: dataObject });
        if (dt.data.status === 'success') {
          return { success: true };
        } else {
          throw new Error(dt.data.message || 'Unknown error');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error(err);
        return { success: false };
      }
    },
  /**
   * Send for review (single task) - moved from TaskUploadModal.vue
   * @param {Object} payload { id, viewtaskdata, remark }
   */
    async sendForReview({ commit }, { id, viewtaskdata, remark, suppressSnackbar = false }) {
      if (!viewtaskdata || !viewtaskdata._id) {
        commit('sbError', 'Task data is missing or invalid.', { root: true });
        return { error: 'Task data is missing or invalid.' };
      }
      let url = `/v2/financialclose/analysis/sendforreview/${id}/c/${viewtaskdata._id}`;
      try {
        const dt = await $axios.post(url, { data: { remark } });
        if (dt.data.status === 'success') {
          if (!suppressSnackbar) {
            commit('sbSuccess', 'Remarks added successfully', { root: true });
          }
          return { success: true };
        } else {
          // Return backend error message
          return { error: dt.data.message || 'Unknown error' };
        }
      } catch (err) {
        // Return error message from backend or generic
        console.error(err);
        return { error: err.message || err || 'Unknown error!' };
      }
   },

  /**
   * Send for review (bulk) - moved from TaskUploadModal.vue
   * @param {Object} payload { id, selectItem, localFileremark, viewtaskdata }
   */
    async sendForReviewBulk({ commit }, { id, selectItem, localFileremark, suppressSnackbar = false }) {
      const tasklistid = (selectItem || []).map((item) => item._id);
      let url = '/v2/financialclose/analysis/bulksendforreview';
      try {
        const dt = await $axios.post(url, {
          data: { remark: localFileremark, pid: id, cidList: tasklistid },
        });
        if (dt.data.status === 'success') {
          if (!suppressSnackbar) {
            commit('sbSuccess', 'Tasklist sent for review', { root: true });
          }
        } else {
          throw new Error(dt.data.message || 'Unknown error');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error(err);
      }
    },
  /**
   * Upload files for a task (moved from TaskUploadModal.vue)
   * @param {Object} payload { files, taskData, projectId, isFileValid }
   */
    async uploadFiles({ commit, state }, { files, taskData, remark, projectId, isFileValid, suppressSnackbar = false }) {
      // Validate files
      let invalidFiles = (files || []).filter(f => !isFileValid(f));
      let totalSize = 0;
      (files || []).forEach(f => { totalSize += f.size || 0; });
      const MAX_TOTAL_SIZE = 25 * 1024 * 1024;
      if (totalSize > MAX_TOTAL_SIZE) {
        commit('sbError', `Total file size exceeds the limit (${(MAX_TOTAL_SIZE / (1024 * 1024)).toFixed(2)} MB)`, { root: true });
        return;
      }
      if (invalidFiles.length > 0) {
        commit('sbError', 'Cannot upload: One or more selected files are invalid. Please remove invalid files before uploading.', { root: true });
        return;
      }
      let arrayPush = [];
      let validFiles = files.filter(f => isFileValid(f));
      if (Array.isArray(validFiles) && validFiles.length > 0) {
      const $novaLocal = getNova();
      if (!$novaLocal || typeof $novaLocal.uploadFile !== 'function') {
          commit('sbError', 'File upload service not initialized', { root: true });
          return;
        }
        const uploadPromises = validFiles.map(async (file) => {
          try {
            let dt = await $novaLocal.uploadFile($axios, file, {});
            arrayPush.push(dt);
          } catch (err) {
            commit('sbError', err.message || err || 'Unknown error!', { root: true });
            console.error(err);
          }
        });
        await Promise.all(uploadPromises);
        if (arrayPush.some((response) => response.data.status === 'success')) {
          let dataArray = arrayPush.flatMap((response) =>
            response.data.data.map((item) => {
              return {
                filename: item.uploadname,
                file_id: item._id,
                file_path: item.path,
              };
            })
          );
          let data = {
            upload: dataArray,
            project: projectId,
            checklist: taskData,
            bulkupload: false,
            remark: remark || '',
          };
          try {
            let response = await $axios.post(
              '/v2/financialclose/analysis/storeworkpaper',
              {
                data: [data],
              }
            );
            if (response.data.status === 'success') {
              if (!suppressSnackbar) {
                commit('sbSuccess', 'File Uploaded', { root: true });
              }
              commit('SET_DRAWER_VISIBLE', false);
              commit('SET_SEND_FOR_REVIEW_DRAWER', false);
              // Apply reconciliation flag (deduplicated logic)
              const checklistId = taskData && (taskData.project || taskData.checklist_id || taskData.project_id);
              applyReconFlag({ commit, state }, { responseData: response.data, tasks: taskData, checklistId });
            } else {
              throw new Error(response?.data?.message || 'Error uploading file');
            }
          } catch (axiosError) {
            commit('sbError', axiosError.message || axiosError || 'Unknown error!', { root: true });
            console.error(axiosError);
          }
        } else {
          commit('sbError', 'Unable to upload file', { root: true });
        }
      } else {
        commit('sbError', 'No valid files to upload', { root: true });
      }
    },
  /**
   * Change checklist status (approve/reject) for a task
   * @param {{ checklistId: string, taskId: string, status: string }} payload
   */
    async changeChecklistStatus({ commit }, { checklistId, taskId, status, approver_remark }) { 
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      let idtxt = taskId;
      let data = { type: status, approver_remark };
      try {
        const response = await $axios.post(
          `/v2/financialclose/analysis/changecheckliststage/${checklistId}/a/${idtxt}`,
          { data: [data] }
        );
        if (response.data.status === "success") {
          commit("sbSuccess", "Status Updated", { root: true });
          return true;
        } else {
          throw response.data.message || "Error changing status";
        }
      } catch (err) {
        commit("sbError", err.message || err || "Unknown error!", { root: true });
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchCustomLayout({ commit, state }, { pageName, forceRefresh = false }) {
      if (!forceRefresh && Object.prototype.hasOwnProperty.call(state.customLayouts, pageName)) {
        return state.customLayouts[pageName];
      }
      const payload = { page: pageName };
      const response = await $axios.post(
        "/v2/financialclose/analysis/getcustomlayout",
        { data: payload }
      );
      if (response?.data?.status === "success" && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const layoutData = { ...response.data.data[0], page: pageName };
        commit('SET_CUSTOM_LAYOUT_PAGE', { page: pageName, data: layoutData });
        return layoutData;
      }
      commit('SET_CUSTOM_LAYOUT_PAGE', { page: pageName, data: null });
      return null;
    },
    /**
     * Preload all Tasklist page APIs into Vuex (formdata, details, checklist, custom layout).
     * Skips network calls when cached data is already available unless forceRefresh is true.
     */
    async ensureTasklistPageData({ dispatch }, { checklistId, pageName, forceRefresh = false }) {
      const [formData, taskListData, customLayout] = await Promise.all([
        dispatch('fetchFormDataV3', { forceRefresh }),
        dispatch('getAllTasks', { checklistId, forceRefresh }),
        dispatch('fetchCustomLayout', { pageName, forceRefresh }),
      ]);
      return { formData, taskListData, customLayout };
    },
    async fetchStatusDetails({ commit }, { metadataId }) {
      const response = await $axios.post(
        `/v2/financialclose/analysis/getduedayswisecount/${metadataId}`
      );
      if (response?.data?.status === "success" && Array.isArray(response.data.data) && response.data.data.length > 0) {
        commit('SET_STATUS_DETAILS', response.data.data[0]);
        return response.data.data[0];
      } else {
        commit('SET_STATUS_DETAILS', null);
        return null;
      }
    },
    async fetchTransactionMatchingAnalysis({ commit }, { checklistId }) {
      const response = await $axios.post(
        `/v2/bot/analysis/getTransactionMatchingAnalysis/${checklistId}`
      );
      if (response?.data?.status === "success" && Array.isArray(response.data.data) && response.data.data.length > 0) {
        commit('SET_TRANSACTION_MATCHING_LIST', response.data.data);
        return response.data.data;
      } else {
        commit('SET_TRANSACTION_MATCHING_LIST', null);
        return null;
      }
    },
    /**
     * Download bulk edit template for selected cycle tasklist items.
     * Returns raw axios response with blob body and headers.
     */
    async downloadBulkEditTasklistTemplate({ rootState }, { checklistId, taskIds }) {
      const cid = rootState.client || rootState.user?.client || '';
      const response = await $axios.post(
        `/v2/financialclose/analysis/downloadBulkEditTasklistTemplate/${checklistId}`,
        { taskIds },
        {
          params: { cid },
          responseType: 'blob',
        }
      );
      return response;
    },
    /**
     * Fetch all tasks and related data for a checklist (Tasklist page)
     * Phase 2: Uses split APIs — /v3/getdetails (metadata/summary) + /v3/getchecklist (tasks).
     * getDetails is now slimmed (no checklist[], no tb[]).
     * checklist is fetched separately via fetchChecklist.
     * Eliminates client-side calculateDetails() and the 7-API waterfall.
     */
    async getAllTasks({ commit, dispatch, state }, { checklistId, forceRefresh = false }) {
      if (
        !forceRefresh
        && state.taskListData?.metadata?._id === checklistId
      ) {
        return state.taskListData;
      }

      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        // Phase 2: Split into parallel calls - metadata+summary from getDetails, tasks from getchecklist
        const [analysisResponse, checklistResult] = await Promise.all([
          $axios.post(`/v3/financialclose/analysis/getdetails/${checklistId}`, { page: 'tasklist' }),
          dispatch('fetchChecklist', { checklistId }),
        ]);

        const dt = analysisResponse.data;
        if (dt.status !== 'success' || !dt.data) throw new Error('No analysis data');
        const data = dt.data;

        const metadata = data.metadata || {};
        const summary = data.summary || { manager: {}, preparer: {}, approver: {} };
        const filteredLocations = data.filteredLocations || [];
        const statusDetails = data.statusDetails || null;

        // Use checklist and schema from the dedicated endpoint
        let tasklistData = checklistResult.checklist || [];
        let customColumns = checklistResult.customColumns || {};
        let customColumnSchema = checklistResult.customColumnSchema || [];
        console.log('[getAllTasks] customColumnSchema from fetchChecklist:', customColumnSchema.length, 'entries');

        if (tasklistData.length === 0) {
          console.warn('[getAllTasks] API returned empty checklist.');
        }

        // Get formdata from cache or fetch (provides locations, users, categories)
        const formData = state.formDataV3 || await dispatch('fetchFormDataV3');
        const locationList = formData?.locations || [];
        const usersList = (formData?.users || []).filter(u => u.isActive);
        // COA-only categories (for Account Reconciliation tasks)
        const coaCategoryAcc = formData?.coaCategoryAcc || [];
        const coaSubcategoryAcc = formData?.coaSubcategoryAcc || [];
        // Merged categories: COA + free-text (for Normal/Checklist tasks)
        const categoryAcc = formData?.categoryAcc || [];
        const subcategoryAcc = formData?.subcategoryAcc || [];

        // Build cache data (backward-compatible shape for existing computed properties)
        const clonedTasklistData = JSON.parse(JSON.stringify(tasklistData));

        const cacheData = {
          metadata,
          checklist: clonedTasklistData,
          pbcitems: [], // PBC no longer returned by v3
          summary,
          locationList,
          usersList,
          locationThresholds: [], // Thresholds now embedded in __computed per task
          infomangerrequests: [], // PBC requests removed from tasklist flow
          chartAcc: [], // Categories now come from formdata
          coaCategoryAcc,
          coaSubcategoryAcc,
          categoryAcc,
          subcategoryAcc,
          customColumns,
          customColumnSchema
        };
        commit('SET_TASKLIST_DATA', cacheData);
        commit('SET_FILTERED_LOCATIONS', { locations: filteredLocations, cycleId: checklistId });
        commit('SET_STATUS_DETAILS', statusDetails);
        commit('SET_TRANSACTION_MATCHING_LIST', null); // TM data embedded per-task now

        // schedulerEnabled comes from formdata (already set in fetchFormDataV3)
        if (!state.notificationEnabled && formData?.schedulerEnabled) {
          commit('SET_NOTIFICATION_ENABLED', formData.schedulerEnabled);
        }

        return cacheData;
      } catch (err) {
        commit('SET_ERROR', err.message || err || 'Unknown error!');
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Phase 2: Fetch checklist separately from getDetails.
     * Calls /v3/financialclose/analysis/getchecklist/:id
     * Frontend does NOT pass pagination/filters/sort — fetches all tasks for client-side processing.
     */
    async fetchChecklist({ dispatch, state }, { checklistId }) {
      try {
        const formData = state.formDataV3 || await dispatch('fetchFormDataV3');
        const response = await $axios.post(`/v3/financialclose/analysis/getchecklist/${checklistId}`, {});
        const dt = response.data;
        if (dt.status !== 'success' || !dt.data) throw new Error('Failed to fetch checklist');
        const data = dt.data;
        // records contains the enriched checklist array (same shape as Phase 1 checklist[])
        let tasklistData = data.records || data.checklist || [];

        // Flatten custom fields from object to top-level keys per task
        let customColumns = {};
        tasklistData = tasklistData.map(item => {
          const cf = item.customfields;
          if (cf && typeof cf === 'object' && !Array.isArray(cf)) {
            if (!Object.keys(customColumns).length) customColumns = { ...cf };
            return { ...item, ...cf };
          }
          if (Array.isArray(cf)) {
            const flatCF = Object.assign({}, ...cf);
            if (!Object.keys(customColumns).length) customColumns = { ...flatCF };
            return { ...item, ...flatCF };
          }
          return item;
        });

        // Attach __computed fields to top-level task properties for backward compat
        tasklistData.forEach(task => {
          const computed = task.__computed || {};
          task.threshold = computed.threshold ?? 'NA';
          task.locationName = computed.locationName || '';
          task.alltaskassigned = computed.alltaskassigned ?? ((task.preparer || []).length > 0 && (task.approver || []).length > 0);
          task.__tbtotals = computed.tbtotals || { total: 0, items: [] };
          task.__tmIndicator = computed.tmIndicator || null;
          task.bot_details = computed.bot_details || {};
          task.bot_analysis_detail = computed.bot_analysis_detail || {};
          task.bot_recon_summary = computed.bot_recon_summary || [];
          if(task.__computed && task.__computed.bot_recon_schedule_amount !== undefined){
             task.bot_recon_schedule_amount = computed.bot_recon_schedule_amount || 0;
          }
          if(task.__computed && task.__computed.bot_recon_explained_difference !== undefined){
             task.bot_recon_explained_difference = computed.bot_recon_explained_difference || 0;
          }
          if(task.__computed && task.__computed.bot_recon_unexplained_difference !== undefined){
             task.bot_recon_unexplained_difference = computed.bot_recon_unexplained_difference || 0;
          }
          
        });

        //  Extract custom column schema (array of { key, label }) from formData.clientCustomColumns for dynamic rendering in Tasklist and Details pages
        let customColumnSchema = formData?.clientCustomColumns || [];
        return { checklist: tasklistData, customColumns, customColumnSchema };
      } catch (err) {
        console.error('[tasks/fetchChecklist]:', err);
        throw err;
      }
    },

    /**
     * Phase 2: Fetch paginated trial balance data for GL picker infinite scroll.
     * Calls /v3/financialclose/analysis/getglaccounts/:id
     * @param {Object} payload { projectId, page, limit, search, location, append }
     */
    async fetchTbPage({ commit, state }, { projectId, page = 1, limit = 50, search = null, searchFields = null, location = null, append = false }) {
      if (state.tbLoading) return;
      commit('SET_TB_LOADING', true);
      try {
        const body = {
          pagination: { page, limit },
        };
        if (search) body.search = search;
        if (searchFields) body.searchFields = searchFields;
        if (location) body.location = location;

        const response = await $axios.post(`/v3/financialclose/analysis/getglaccounts/${projectId}`, body);
        const dt = response.data;
        if (dt.status !== 'success' || !dt.data) throw new Error('Failed to fetch TB data');
        const data = dt.data;
        const records = data.records || [];
        const totalCount = data.totalCount || 0;
        const totalPages = data.totalPages || 0;

        commit('SET_TB_RECORDS', { records, append });
        commit('SET_TB_PAGINATION', { page, limit, totalCount, totalPages });
        commit('SET_TB_FULLY_LOADED', page >= totalPages || records.length < limit);
        return data;
      } catch (err) {
        console.error('[tasks/fetchTbPage]:', err);
        commit('sbError', err?.message || 'Failed to fetch trial balance data', { root: true });
        return null;
      } finally {
        commit('SET_TB_LOADING', false);
      }
    },

    /**
     * Fetch a specific task by ID
     * With v3, __computed fields (tbtotals, threshold, locationName) are already
     * attached to each task in the checklist — no client-side calculateDetails needed.
     * Also ensures formDataV3 is loaded for userMap/locationMap usage by child components.
     */
    async fetchTask({ commit, dispatch, state }, { checklistId, taskId }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        // Ensure formDataV3 is available (cached if already fetched)
        if (!state.formDataV3) {
          await dispatch('fetchFormDataV3');
        }
        // Refresh all related data
        const allData = await dispatch('getAllTasks', { checklistId });
        // Find the task in the checklist (already enriched with __computed by v3)
        const task = (allData.checklist || []).find(t => t._id === taskId);
        if (!task) {
          throw new Error('Task not found in checklist');
        }
        // Enrich the task with context data for the detail page
        const enrichedTask = {
          ...task,
          usersList: allData.usersList || [],
          locationList: allData.locationList || [],
          locationThresholds: [],
          infomangerrequests: [],
          chartAcc: [],
          customColumns: allData.customColumns || [],
          tb: [],
          pbcitems: [],
          summary: allData.summary || {},
          metadata: allData.metadata || {},
          checklist: allData.checklist || [],
        };
        // Threshold is already set from __computed in getAllTasks
        commit('CACHE_TASK', enrichedTask);
        commit('SET_CURRENT_TASK', enrichedTask);
        return enrichedTask;
      } catch (error) {
        const errorMessage = 'An error occurred while fetching task details';
        commit('SET_ERROR', errorMessage);
        console.error(errorMessage, error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Update a task
     */
    async updateTask({ commit }, { checklistId }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        // No update API, just refresh all tasks
        const allTasks = await this.dispatch('tasks/getAllTasks', { checklistId });
        return allTasks;
      } catch (error) {
        const errorMessage = 'An error occurred while updating task (refreshing all tasks)';
        commit('SET_ERROR', errorMessage);
        if (error && error.message) {
          console.error(errorMessage, error.message);
        } else {
          console.error(errorMessage, error);
        }
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchNotificationEnabled({ commit, rootState }) {
      try {
        const clientId = rootState.client;
        const response = await $axios.post(`/v2/client/get/${clientId}`);
        if (
          response?.data &&
          response?.data?.status === "success" &&
          response.data.data &&
          response.data.data.length
        ) {
          const enabled = response.data.data[0]?.schedulerEnabled;
          commit('SET_NOTIFICATION_ENABLED', enabled);
        } else {
          commit('SET_NOTIFICATION_ENABLED', false);
        }
      } catch (err) {
        commit('SET_NOTIFICATION_ENABLED', false);
        // Optionally handle error
      }
    },

  /**
   * Get all task overdue email notifications for a project
   * @param {Object} payload - Contains cycleId
   * @returns {Promise} - Promise resolving to the email notifications
   */
    async getAllTaskEmailNotifications({ commit }, { cycleId }) {
      commit('SET_LOADING', true);
      
      try {
        const response = await $axios.post(
          `${API_ENDPOINTS.GET_ALL_SCHEDULER}/${cycleId}`,
          { notificationType: EMAIL_NOTIFICATION.FEATURE_TYPES.TASK_OVERDUE }
        );
        
        if (response?.data && response?.data?.status === "success") {
          const isCycleInActive = response?.data?.message?.trim().toLowerCase() === 'cycle is not active';
          commit('SET_CYCLE_STATUS', isCycleInActive);
          commit('SET_EMAIL_NOTIFICATIONS', response.data.data || []);
          return response.data.data || [];
        } else {
          const errorMsg = response.data?.message || "Error retrieving notifications";
          commit('SET_ERROR', errorMsg);
          // Also commit to global snackbar if needed
          commit('sbError', errorMsg, { root: true });
          return [];
        }
      } catch (err) {
        console.error(err);
        commit('SET_ERROR', err.message || "Unknown error fetching notifications");
        commit('sbError', err.message || "Unknown error fetching notifications", { root: true });
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
  /**
   * Add a new task overdue email notification
   * @param {Object} payload - Contains notification details
   * @returns {Promise} - Promise resolving to the API response
   */
    async addTaskEmailNotification({ commit, state }, { cycleId, notificationData }) {
      // Check if maximum limit reached
      if (state.emailNotifications.length >= EMAIL_NOTIFICATION.MAX_EMAIL_INSTANCES) {
        const errorMsg = `Current cycle already have maximun notifications. Cannot add more`;
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      }
      
      commit('SET_LOADING', true);
      
      const data = {
        feature: EMAIL_NOTIFICATION.FEATURE_TYPES.TASK_OVERDUE,
        flag: true,
        type: EMAIL_NOTIFICATION.TYPE,
        subject: notificationData.subject,
        mailBody: notificationData.mailBody,
        cycle_id: cycleId.toString(),
        schedulerTiming: CRON.hourToCronFormat(notificationData.time),
        sla: notificationData.sla,
        priority: notificationData.priority,
        meta: {
          location_key: notificationData.locations,
          toUsers: notificationData.emails,
          start: notificationData.startDate,
          end: notificationData.endDate,
          startDate: notificationData.startDate,
          endDate: notificationData.endDate,
          sla: notificationData.sla,
          category: notificationData.category,
          priority: notificationData.priority,
        }
      };
      
      try {
        // Check for duplicates
        const isDuplicate = state.emailNotifications.some(
          (item) =>
            item?.meta?.location_key === notificationData.locations &&
            item?.meta?.category === notificationData.category &&
            item?.meta?.sla === notificationData.sla
        );

        if (isDuplicate) {
          const errorMsg = `Notification already exists for this Location, SLA and Category combo`;
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }

        const response = await $axios.post(
          `${API_ENDPOINTS.ADD_TASK_OVERDUE_NOTIFICATION}/${cycleId}`,
          { featureDetails: data }
        );
        
        if (response?.data?.status === "success") {
          commit('sbSuccess', response.data.message || "Email notification created", { root: true });
          return { success: true, data: response.data };
        } else {
          const errorMsg = response.data?.message || "Error creating notification";
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error creating notification";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Fetch distinct non-AR category/subcategory values from master tasks
     * of the given cycle_type and merge with COA base lists.
     * @param {Object} payload - { cycleType, coaCategoryAcc, coaSubcategoryAcc }
     * @returns {Promise<{ categoryAcc: string[], subcategoryAcc: string[] }>}
     */
    async fetchMasterCategories({ commit }, { cycleType, level = 'master', coaCategoryAcc = [], coaSubcategoryAcc = [] }) {
      try {
        const response = await $axios.post('/v2/financialclose/analysis/uniquecategories', {
          cycle_type: cycleType.toUpperCase(),
          level,
        });
        let categoryAcc = [...coaCategoryAcc];
        let subcategoryAcc = [...coaSubcategoryAcc];
        if (response?.data?.status === 'success' && response.data.data) {
          const { categories = [], subcategories = [] } = response.data.data;
          const catSet = new Set(categoryAcc);
          const subcatSet = new Set(subcategoryAcc);
          categories.forEach(c => { if (c) catSet.add(c); });
          subcategories.forEach(s => { if (s) subcatSet.add(s); });
          categoryAcc = [...catSet];
          subcategoryAcc = [...subcatSet];
        }
        commit('SET_MASTER_CATEGORIES', { categoryAcc, subcategoryAcc });
        return { categoryAcc, subcategoryAcc };
      } catch (err) {
        console.error('[tasks/fetchMasterCategories] error:', err);
        commit('sbError', err.message || 'Failed to fetch master categories', { root: true });
        return { categoryAcc: coaCategoryAcc, subcategoryAcc: coaSubcategoryAcc };
      }
    },

    async getAllUsersWithStatus({ commit },{isActiveUsersRequired =true}) {
      try {
        const response = await $axios.post("/v2/client/getAllUsersForEntity",{usersStatus:isActiveUsersRequired});
        if (response?.data?.status !== "success") {
          const errorMsg = response.data?.message || MESSAGES.FAILED_TO_GET_USERS;
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
        }
        const mutationType = isActiveUsersRequired ? 'SET_ACTIVE_USERS' : 'SET_INACTIVE_USERS';
        if (!Array.isArray(response.data.data) || response.data.data.length === 0) {
          commit(mutationType, []);
        }
        commit(mutationType, response.data.data || []);
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error retrieving users";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
      }
    },

  /**
   * Update an existing task overdue email notification
   * @param {Object} payload - Contains notification details with _id
   * @returns {Promise} - Promise resolving to the API response
   */
    async updateTaskEmailNotification({ commit }, { cycleId, notificationData }) {
      if (!notificationData._id) {
        const errorMsg = "Notification ID is required for update";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      }
      
      commit('SET_LOADING', true);
      
      const data = {
        feature: EMAIL_NOTIFICATION.FEATURE_TYPES.TASK_OVERDUE,
        flag: true,
        type: EMAIL_NOTIFICATION.TYPE,
        subject: notificationData.subject,
        mailBody: notificationData.mailBody,
        schedulerTiming: CRON.hourToCronFormat(notificationData.time),
        cycle_id: cycleId.toString(),
        _id: notificationData._id,
        sla: notificationData.sla,
        priority: notificationData.priority,
        meta: {
          location_key: notificationData.locations,
          toUsers: notificationData.emails,
          start: notificationData.startDate,
          end: notificationData.endDate,
          startDate: notificationData.startDate,
          endDate: notificationData.endDate,
          sla: notificationData.sla,
          category: notificationData.category,
          priority: notificationData.priority,
        }
      };
      
      try {
        const response = await $axios.post(
          `${API_ENDPOINTS.EDIT_TASK_OVERDUE_NOTIFICATION}/${cycleId}`,
          { featureDetails: data }
        );
        
        if (response?.data?.status === "success") {
          commit('sbSuccess', response.data.message || "Email notification updated", { root: true });
          return { success: true, data: response.data };
        } else {
          const errorMsg = response.data?.message || "Error updating notification";
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error updating notification";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
  /**
   * Enable or disable a task overdue email notification
   * @param {Object} payload - Contains notification item and enable/disable action
   * @returns {Promise} - Promise resolving to the API response
   */
    async toggleTaskEmailNotificationStatus({ commit, state }, { cycleId, notification, enable }) {
      if (!notification?._id) {
        const errorMsg = "Notification ID is required for status change";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      }
      
      commit('SET_LOADING', true);
      
      // Find the current notification in state to get all properties
      const currentItem = state.emailNotifications.find(
        item => item?._id.toString() === notification?._id.toString()
      );
      
      if (!currentItem) {
        const errorMsg = "Notification not found";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      }
      
      const data = {
        feature: notification.feature,
        flag: enable,
        type: EMAIL_NOTIFICATION.TYPE,
        _id: notification._id,
        subject: notification.subject,
        mailBody: notification.mailBody,
        cycle_id: notification.cycle_id,
        schedulerTiming: notification.schedulerTiming,
        isEnableDisable: true, // Flag to indicate this is an enable/disable action
        sla: currentItem?.sla ?? notification?.sla ?? currentItem.meta?.sla ?? notification.meta?.sla ?? '',
        priority: currentItem?.priority ?? notification?.priority ?? currentItem.meta?.priority ?? notification.meta?.priority ?? 'High',
        meta: {
          location_key: notification.meta?.location_key,
          toUsers: notification.meta?.toUsers,
          start: notification.meta?.start ?? notification.meta?.startDate,
          end: notification.meta?.end ?? notification.meta?.endDate,
          startDate: notification.meta?.start ?? notification.meta?.startDate,
          endDate: notification.meta?.end ?? notification.meta?.endDate,
          sla: currentItem?.sla ?? notification?.sla ?? currentItem.meta?.sla ?? notification.meta?.sla ?? '',
          category: currentItem.meta?.category ?? "",
          priority: currentItem?.priority ?? notification?.priority ?? currentItem.meta?.priority ?? notification.meta?.priority ?? 'High',
        }
      };
      
      try {
        const response = await $axios.post(
          `/v2/financialclose/analysis/editTaskOverdueNotification/${cycleId}`,
          { featureDetails: data }
        );
        
        if (response?.data?.status === "success") {
          const successMsg = enable ? "Notification Enabled" : "Notification Disabled";
          commit('sbSuccess', successMsg, { root: true });
          return { success: true, data: response.data };
        } else {
          const errorMsg = response.data?.message || "Error updating notification status";
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error updating notification status";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async triggerTaggedNotification({ commit }, { taggedUsers, msg, task }) {
      try {
        const payload = {
          data: {
            commenter: this.state.user.name || this.state.user.email,
            commentText: msg,
            taskUrl: baseurl + `/financialclose/project/checklist/${task.project}/task/${task._id}?cid=${this.state.client}`,
            taskReference: task.ref || '',
            taskLocation: task.locationName || '',
            taggedUsers,
          },
        };
        await $axios.post('/v2/financialclose/analysis/triggerTaggedNotification', payload, {
          headers: { 'Content-Type': 'application/json' },
        });
        commit('sbSuccess', 'Tagged notification sent!', { root: true });
        return { success: true };
      } catch (err) {
        commit('sbError', err.message || 'Failed to send tagged notification', { root: true });
        return { success: false, message: err.message };
      }
    },
    /**
     * Bulk update skipValidations (threshold enforcement) for a set of tasks.
     * @param {Object} payload { cycleId, taskIds, skipValidations, client }
     * @returns {{ success: boolean, message?: string }}
     */
    async bulkUpdateSkipValidations({ commit }, { cycleId, taskIds, skipValidations, client }) {
      try {
        const response = await $axios.post(
          `/v2/financialclose/analysis/bulkUpdateSkipReconValidations/${cycleId}`,
          {
            data: [{ skipValidations, task_ids: taskIds }],
            client,
          }
        );
        if (response?.data?.status === 'success') {
          commit('sbSuccess', 'Threshold enforcement updated successfully', { root: true });
          return { success: true };
        } else {
          const msg = response?.data?.message || 'Failed to update threshold enforcement';
          commit('sbError', msg, { root: true });
          return { success: false, message: msg };
        }
      } catch (err) {
        console.error('[StoreAction]: bulkUpdateSkipValidations error', err);
        commit('sbError', err?.response?.data?.message || err?.message || 'Failed to update threshold enforcement', { root: true });
        return { success: false, message: err?.message };
      }
    },
    saveHorizonViewState({ commit }, { checklistId, partial }) {
      commit('SET_HORIZON_VIEW_STATE', { checklistId, partial });
    },
    /**
     * Bulk update due date for a set of tasks.
     * @param {Object} payload { cycleId, taskIds, duedate, note? }
     * @returns {{ success: boolean, message?: string }}
     */
    async bulkUpdateDueDate({ commit }, { cycleId, taskIds, duedate, note }) {
      try {
        const body = { duedate, _id: taskIds };
        if (note) {
          body.note = note;
        }
        const response = await $axios.post(
          `/v2/financialclose/analysis/bulkupdateduedate/${cycleId}`,
          { data: body }
        );
        if (response?.data?.status === 'success') {
          commit('sbSuccess', 'Updated Due Date', { root: true });
          return { success: true };
        } else {
          const msg = response?.data?.message || 'Failed to update due date';
          commit('sbError', msg, { root: true });
          return { success: false, message: msg };
        }
      } catch (err) {
        console.error('[StoreAction]: bulkUpdateDueDate error', err);
        commit('sbError', err?.response?.data?.message || err?.message || 'Failed to update due date', { root: true });
        return { success: false, message: err?.message };
      }
    },
  },
  
  getters: {
    getCustomLayout: (state) => (pageName) => (
      pageName ? state.customLayouts[pageName] ?? null : null
    ),
    getStatusDetails: (state) => state.statusDetails,
    getTransactionMatchingList: (state) => state.transactionMatchingList,
    /**
     * Get a task by ID
     */
    getTaskById: (state) => (taskId) => {
      // Prefer currentTask when it matches. This prevents stale cached data from
      // overriding the task detail page state during role switching / refresh flows.
      if (state.currentTask && state.currentTask._id === taskId) {
        return state.currentTask;
      }
      if (state.tasks[taskId]) return state.tasks[taskId];

      // Fallback to taskListData if present (covers cases where getAllTasks refreshed
      // the checklist but the per-task cache hasn't been populated yet).
      const checklist = state.taskListData && Array.isArray(state.taskListData.checklist)
        ? state.taskListData.checklist
        : [];
      return checklist.find(t => t && t._id === taskId) || null;
    },
    
    /**
     * Get all tasks for a checklist
     */
    getTaskList: (state) => (checklistId) => {
      return state.taskLists[checklistId] || [];
    },
    // Get user details with status
    getUserDetails:(state) => state.userDetails || {},
    getActiveUsersList:(state) => state.activeUsers || [],
    getActiveUsersIds:(state) => state.activeUsers.map(user => user?._id || user?.id).filter(Boolean) || [],
    getInactiveUsersList:(state) => state.inactiveUsers || [],
    getMasterCategories: (state) => state.masterCategories || { categoryAcc: [], subcategoryAcc: [] },

    /**
     * Get loading status
     */
    isLoading: (state) => state.loading,
    
    /**
     * Get error status
     */
    getError: (state) => state.error,
    
    /**
     * Get current task
     */
    getCurrentTask: (state) => state.currentTask,
    
    /**
     * Get cached task list data
     */
    getTaskListData: (state) => {
      return state.taskListData;
    },
    isNotificationEnabled: (state) => state.notificationEnabled,
    /**
     * Get email notifications
     */
    getTaskEmailNotifications: (state) => state.emailNotifications,
    /**
     * Get cycle inactive status
     */
    isCycleInactive: (state) => state.isCycleInActive,

    getUsersList: (state) => state.usersList,

    /**
     * O(1) lookup map for user ID → user object { _id, name, email, isActive }.
     * Built from formDataV3 (v3 consolidated endpoint) or legacy usersList.
     * Components should use this instead of .find() for ID→name resolution.
     */
    getUserMap: (state) => {
      const users = state.formDataV3?.users || state.taskListData?.usersList || [];
      if (!Array.isArray(users)) return {};
      const map = {};
      for (const u of users) {
        if (u && u._id) map[u._id] = u;
      }
      return map;
    },

    /**
     * O(1) lookup map for user email → user _id.
     * Used for bulk upload email→ID resolution.
     */
    getUserEmailMap: (state) => {
      const users = state.formDataV3?.users || state.taskListData?.usersList || [];
      if (!Array.isArray(users)) return {};
      const map = {};
      for (const u of users) {
        if (u && u.email) map[u.email.trim()] = u._id;
      }
      return map;
    },

    /**
     * O(1) lookup map for location ID → location object { _id, name, ... }.
     * Components should use this instead of .find() for location resolution.
     */
    getLocationMap: (state) => {
      const locations = state.formDataV3?.locations || state.taskListData?.locationList || [];
      if (!Array.isArray(locations)) return {};
      const map = {};
      for (const loc of locations) {
        if (loc && loc._id) map[loc._id] = loc;
      }
      return map;
    },
    
    /**
     * Get location list from cached task data
     */
    getLocationList: (state) => state.locationList || [],
    
    /**
     * Get enabled locations for dropdown
     */
    getEnabledLocations: (state) => state.enabledLocations || [],

    /**
     * Get filtered locations (cycle-scoped intersection of selected locations with all locations)
     */
    getFilteredLocations: (state) => state.filteredLocations || [],

    /**
     * Get v3 formdata (client-level reference data: users, locations, categories)
     */
    getFormDataV3: (state) => state.formDataV3,

    getHorizonViewState: (state) => (checklistId) => state.horizonViewState[checklistId] || null,

    getTasklistView: (state) => (checklistId) => state.tasklistViewByChecklist[checklistId] || null,
    
    /**
     * Get cycle metadata from cached task data
     * Used to avoid redundant dashboard API calls
     */
    getCycleMetadata: (state) => state.taskListData?.metadata || null,
    
    /**
     * Get role-specific summaries
     */
    getManagerSummary: (state) => state.managersummary || {},
    getPreparerSummary: (state) => state.preparersummary || {},
    getApproverSummary: (state) => state.approversummary || {},

    // TB pagination getters (Phase 2)
    getTbRecords: (state) => state.tbRecords,
    getTbPagination: (state) => state.tbPagination,
    isTbLoading: (state) => state.tbLoading,
    isTbFullyLoaded: (state) => state.tbFullyLoaded,
  }
};